import { getTheatrePaymentInfo } from '../services/data';
import { getOwnedGiftCards } from '../services/giftCards';
import { types } from '../types';
import { allocateGiftCards, clampGiftCards, giftCardError, rubles } from '../helpers/giftCards';

export default {
  namespaced: true,
  state: {
    cards: [], selected: [], enabled: true, allowed: null,
    ready: false, busy: false, error: '', context: '', revision: 0,
    bonusManual: false, bonusRequested: 0,
  },
  getters: {
    visible: (state, getters, root) => root.user.isAuthorized && !root.isVkWidget &&
      !root.isVkMobileWidget && root.user.paymentType !== 'PushkinFilm' && state.allowed !== false,
    payments(state, getters, root) {
      return state.ready && state.allowed && state.enabled && root.user.isAuthorized &&
        root.user.paymentType !== 'PushkinFilm' && !root.isVkWidget && !root.isVkMobileWidget
        ? state.selected.filter(p => p.AmountRubles > 0) : [];
    },
    amount: (state, getters) => getters.payments.reduce((sum, p) => sum + p.AmountRubles, 0),
    due: (state, getters, root) => Math.max(0, Number(root.cart.sum) - getters.amount),
  },
  mutations: {
    update(state, data) { Object.assign(state, data); },
  },
  actions: {
    async load({ state, commit, dispatch, rootState }, { theatreId, filmId, context, refresh = false }) {
      const revision = state.revision + 1;
      const token = localStorage.getItem('widgetToken');
      const isNew = state.context !== context;
      commit('update', { revision, busy: true, ready: false, error: '' });
      if (isNew) commit('update', { cards: [], selected: [], allowed: null, enabled: true,
        bonusManual: false, bonusRequested: 0 });
      try {
        if (isNew && rootState.user.crmSystem && rootState.user.crmSystem.Accounts) {
          commit(types.SET_CUSTOMER_ACCOUNTS, rootState.user.crmSystem.Accounts, { root: true });
        }
        const orderParts = [];
        if (rootState.cart.tickets.length) orderParts.push('Movie');
        if (rootState.cart.bar.length) orderParts.push('Bar');
        const info = await getTheatrePaymentInfo({ theatreId, filmId, orderParts });
        const result = info.IsGiftCardPaymentAllowed ? await getOwnedGiftCards(theatreId) : { Cards: [] };
        if (state.revision !== revision || token !== localStorage.getItem('widgetToken') || !rootState.user.isAuthorized) return;
        const cards = result.Cards || [];
        const total = Number(rootState.cart.sum) + Number(rootState.cart.usedBonuses);
        const selected = isNew && !refresh ? allocateGiftCards(cards, total) : clampGiftCards(state.selected, cards, total);
        commit('update', { cards, selected, context, allowed: !!info.IsGiftCardPaymentAllowed, ready: true });
        await dispatch('recalculate');
      } catch (error) {
        if (state.revision === revision) commit('update', { error: giftCardError(error), ready: false });
      } finally {
        if (state.revision === revision) commit('update', { busy: false });
      }
    },
    change({ commit, dispatch }, data) {
      commit('update', data);
      return dispatch('recalculate');
    },
    manualBonuses({ commit, dispatch, rootState }) {
      commit('update', { bonusManual: true, bonusRequested: rubles(rootState.cart.usedBonuses) });
      return dispatch('recalculate');
    },
    recalculate({ state, getters, commit, rootState: root }) {
      if (!state.allowed || !state.ready || !root.user.isAuthorized) return;
      const cart = root.cart;
      const total = Number(cart.sum) + Number(cart.usedBonuses);
      commit('update', { selected: clampGiftCards(state.selected, state.cards, total) });
      const accounts = root.customer.accountsList.map(index => root.customer.accounts[index]);
      const account = accounts.find(a => a.selected && !a.blocked && !a.deleted) ||
        accounts.find(a => a.type.systemType === 'Bonuses' && !a.blocked && !a.deleted && a.balance > 0);
      const balance = account ? account.balance : (root.user.card.id ? root.user.card.balance : 0);
      const ticketMax = Math.max(0, Number(cart.ticketSum) - 1);
      const barMax = Math.max(0, Number(cart.barSum) - 1);
      const max = Math.min(rubles(balance), rubles(ticketMax + barMax), rubles(total - getters.amount));
      const amount = root.user.paymentType === 'PushkinFilm' ? 0 :
        Math.min(max, state.bonusManual ? state.bonusRequested : max);
      const tickets = Math.min(ticketMax, amount);
      const bar = amount - tickets;
      // Keep the existing bonus controls in sync with the amount sent to SubmitOrder.
      accounts.forEach(a => {
        a.selected = a === account && amount > 0;
        a.discount = { ...a.discount, value: a === account ? amount : 0,
          tickets: a === account ? tickets : 0, bar: a === account ? bar : 0,
          max: { all: max, tickets: ticketMax, bar: barMax } };
      });
      root.user.paymentWithBonuses = amount > 0;
      root.user.maxBonuses = max;
      cart.usedBonuses = amount;
      cart.spentBonuses = { tickets, bar };
      cart.sum = total - amount;
    },
  },
};
